/**
 * Product Hero JavaScript - Miracles Theme
 * Handles: Sticky Nav, Sticky Buy Bar, Image Gallery, Bundle Selector
 */

class ProductHero {
    constructor() {
        this.stickyNav = document.querySelector('[data-product-nav]');
        this.stickyBuyBar = document.querySelector('[data-sticky-buy-bar]');
        this.foldSection = document.getElementById('rg-fold');
        this.productSection = document.querySelector('[data-product-section]');
        this.anchorChips = document.querySelectorAll('[data-anchor-chip]');
        this.thumbnails = document.querySelectorAll('[data-thumbnail-index]');
        this.mainImage = document.getElementById('rg-main-image');
        this.bundleCards = document.querySelectorAll('[data-bundle-card]');
        this.buyNowButtons = document.querySelectorAll('[data-buy-now-main], [data-buy-now-sticky]');
        this.addToCartButton = document.querySelector('[data-add-to-cart]');
        this.scrollToButtons = document.querySelectorAll('[data-scroll-to]');

        // Get variant ID from section data attribute
        this.currentVariantId = this.productSection?.dataset.variantId || null;
        console.log('✅ ProductHero initialized - Variant ID:', this.currentVariantId);

        this.currentQuantity = 1;
        this.currentTotalPrice = null;
        this.currentUnitPrice = parseInt(this.productSection?.dataset.unitPrice) || 0;
        this.currentCompareAtPrice = parseInt(this.productSection?.dataset.comparePrice) || 0;
        this.hideCompareAt = this.productSection?.dataset.hideCompareAt === 'true';
        this.showCompareAsDiscount = this.productSection?.dataset.showCompareAsDiscount === 'true';
        this.pricePromo = document.querySelector('[data-price-promo]');
        this.promoCode = this.pricePromo?.dataset.promoCode?.trim().toUpperCase() || '';
        this.promoDiscountPercent = parseFloat(this.pricePromo?.dataset.promoDiscountPercent) || 0;
        this.promoActive = false;

        this.init();
    }

    init() {
        this.setupStickyObserver();
        this.setupAnchorNavigation();
        this.setupImageGallery();
        this.setupBundleSelector();
        this.setupQuantitySelector();
        this.setupCartActions();
        this.setupScrollToButtons();
        this.setupVariantPicker();
        this.setupPriceInfoModal();
        this.setupFirstPurchaseOffer();
        this.setupDiscountAppliedListener();
        this.setupOfferSelectorSync();
        this.selectDefaultBundle();
        this.syncPromoFromCart();
    }

    setupOfferSelectorSync() {
        // El bloque "offer_selector" (selector de ofertas/kits) vive fuera del
        // variant_picker normal -> avisa el cambio de variante con este evento
        // en vez de un <select>/<input radio> que setupVariantPicker() escuche.
        document.addEventListener('offer-selector:variant-selected', (event) => {
            this._applyOfferSelectorVariant(event.detail || {});
        });

        // El <offer-selector-final> es un custom element: su connectedCallback
        // corre apenas el navegador parsea la etiqueta, que puede ser ANTES de
        // que este script (cargado aparte) llegue a registrar el listener de
        // arriba. Si ya trae una tarjeta preseleccionada (default_offer), ese
        // primer aviso se pierde y el botón de arriba se queda con el precio
        // base. Por eso, además de escuchar, revisamos el estado ya pintado.
        const preselected = document.querySelector(
            'offer-selector-final[data-external-buy-button="true"] .offer-sf__hit[aria-checked="true"]'
        );
        if (preselected && preselected.dataset.variantId) {
            this._applyOfferSelectorVariant({
                variantId: preselected.dataset.variantId,
                price: parseInt(preselected.dataset.price, 10) || 0,
                compareAtPrice: parseInt(preselected.dataset.compare, 10) || 0
            });
        }
    }

    _applyOfferSelectorVariant(detail) {
        if (!detail.variantId) return;

        this.currentVariantId = detail.variantId;
        if (typeof detail.price === 'number' && detail.price > 0) this.currentUnitPrice = detail.price;
        this.currentCompareAtPrice = typeof detail.compareAtPrice === 'number' ? detail.compareAtPrice : 0;
        if (this.productSection) this.productSection.dataset.variantId = detail.variantId;

        const hiddenInput = document.querySelector('[data-selected-variant]');
        if (hiddenInput) hiddenInput.value = detail.variantId;

        this.renderDisplayedPrice();
        this.syncPromoFromCart();

        const buyBtn = document.querySelector('[data-buy-now-main]');
        if (buyBtn) buyBtn.disabled = false;
        const atcBtn = document.querySelector('[data-add-to-cart]');
        if (atcBtn) atcBtn.disabled = false;
    }

    setupPriceInfoModal() {
        const modal = document.querySelector('[data-price-info-modal]');
        const openButton = document.querySelector('[data-price-info-open]');
        const closeButton = modal?.querySelector('[data-price-info-close]');
        if (!modal || !openButton || !closeButton) return;

        openButton.addEventListener('click', () => {
            if (typeof modal.showModal === 'function') modal.showModal();
            else modal.setAttribute('open', '');
        });

        closeButton.addEventListener('click', () => modal.close());
        modal.addEventListener('click', (event) => {
            if (event.target === modal) modal.close();
        });
    }

    setupFirstPurchaseOffer() {
        const offer = document.querySelector('[data-first-purchase-promo]');
        if (!offer || offer.disabled || offer.hidden) return;

        // Es un test independiente al A/B interno del pop-up (correo vs. OTP).
        // Mezclarlos invalidaría el resultado: aquí solo cambia el copy del CTA.
        const storageKey = 'miracles_pdp_first_purchase_copy';
        let variant = null;
        try {
            variant = window.localStorage.getItem(storageKey);
            if (variant !== 'A' && variant !== 'B') {
                variant = Math.random() < 0.5 ? 'A' : 'B';
                window.localStorage.setItem(storageKey, variant);
            }
        } catch (error) {
            variant = Math.random() < 0.5 ? 'A' : 'B';
        }

        const copies = {
            A: '¿Primera compra? Obtén tu descuento',
            B: 'Obtén tu descuento de bienvenida'
        };
        const copy = offer.querySelector('[data-first-purchase-copy]');
        if (copy) copy.textContent = copies[variant];
        offer.dataset.copyVariant = variant;

        offer.addEventListener('click', () => {
            const popupVariant = window.MiraclesPopup?.instancia?.cfg?.variante || 'desconocida';
            if (typeof window.gtag === 'function') {
                window.gtag('event', 'pdp_first_purchase_offer_clicked', {
                    pdp_copy_variant: variant,
                    popup_variant: popupVariant
                });
            }
            window.MiraclesPopup?.abrir?.();
        });
    }

    setupDiscountAppliedListener() {
        document.addEventListener('miracles:discount-applied', (event) => {
            const appliedCode = String(event.detail?.codigo || '').trim().toUpperCase();
            if (!appliedCode || appliedCode !== this.promoCode) return;

            // Shopify ya recibió la visita a /discount/<código>. Releemos
            // cart.js para confirmar la cookie y actualizar el precio sin
            // forzar navegación ni recarga de la ficha.
            this.syncPromoFromCart();
        });
    }

    getDisplayedUnitPrice() {
        if (!this.promoActive || !this.promoDiscountPercent) return this.currentUnitPrice;
        return Math.round(this.currentUnitPrice * (1 - this.promoDiscountPercent / 100));
    }

    renderDisplayedPrice() {
        const mainPrice = document.querySelector('[data-main-price]');
        const promoOriginalPrice = document.querySelector('[data-promo-original-price]');
        const priceLabel = document.querySelector('[data-price-current-label]');
        const priceNote = document.querySelector('[data-price-current-note]');
        const promoCodeBadge = document.querySelector('[data-promo-code-badge]');
        const firstPurchaseOffer = document.querySelector('[data-first-purchase-promo]');
        const isPromo = this.promoActive && this.promoDiscountPercent > 0;
        const hasComparisonDiscount = this.showCompareAsDiscount
            && this.currentCompareAtPrice > this.currentUnitPrice;
        const showDiscountPriceStyle = isPromo || hasComparisonDiscount;

        if (mainPrice) mainPrice.textContent = this.formatMoneyShopify(this.getDisplayedUnitPrice());
        if (promoOriginalPrice) {
            promoOriginalPrice.textContent = this.formatMoneyShopify(
                hasComparisonDiscount ? this.currentCompareAtPrice : this.currentUnitPrice
            );
            promoOriginalPrice.hidden = !showDiscountPriceStyle;
        }
        if (priceLabel) priceLabel.textContent = showDiscountPriceStyle ? 'PRECIO CON DESCUENTO' : 'PRECIO MIRACLES';
        if (priceNote) priceNote.hidden = showDiscountPriceStyle;
        if (promoCodeBadge) promoCodeBadge.hidden = !isPromo;
        if (firstPurchaseOffer) firstPurchaseOffer.hidden = isPromo;

        this.updateBuyButtonPrice();
    }

    async syncPromoFromCart(cart = null) {
        if (!this.promoCode || !this.promoDiscountPercent) return;

        try {
            const cartRoot = window.Shopify?.routes?.root || '/';
            const cartUrl = `${cartRoot.endsWith('/') ? cartRoot : `${cartRoot}/`}cart.js`;
            const currentCart = cart || await fetch(cartUrl).then(response => response.json());
            const hasCampaignCode = currentCart.discount_codes?.some(discount =>
                String(discount.code || '').trim().toUpperCase() === this.promoCode
            );
            const selectedLine = currentCart.items?.find(item => String(item.variant_id) === String(this.currentVariantId));

            // Antes de agregar, Shopify conserva el código pero no puede marcarlo aplicable sin una línea.
            // Después de agregar, el precio final de la línea es la validación definitiva.
            this.promoActive = selectedLine
                ? Number(selectedLine.final_price) < Number(selectedLine.original_price)
                : Boolean(hasCampaignCode);

            this.renderDisplayedPrice();
        } catch (error) {
            console.warn('No fue posible sincronizar el precio promocional.', error);
        }
    }

    /**
     * Quantity Selector Logic
     */
    setupQuantitySelector() {
        const qtyInputs = document.querySelectorAll('[data-quantity-input]');

        qtyInputs.forEach(input => {
            const container = input.closest('.rg-quantity-input-group');
            if (!container) return;

            const btnDec = container.querySelector('[data-quantity-decrease]');
            const btnInc = container.querySelector('[data-quantity-increase]');

            if (btnDec) {
                btnDec.addEventListener('click', () => {
                    let val = parseInt(input.value) || 1;
                    if (val > 1) {
                        input.value = val - 1;
                        this.currentQuantity = val - 1;
                        this.updateBuyButtonPrice();
                    }
                });
            }

            if (btnInc) {
                btnInc.addEventListener('click', () => {
                    let val = parseInt(input.value) || 1;
                    input.value = val + 1;
                    this.currentQuantity = val + 1;
                    this.updateBuyButtonPrice();
                });
            }

            input.addEventListener('change', () => {
                let val = parseInt(input.value) || 1;
                if (val < 1) val = 1;
                input.value = val;
                this.currentQuantity = val;
                this.updateBuyButtonPrice();
            });
        });
    }

    /**
     * Precio (y precio de comparación) x cantidad seleccionada,
     * en el botón principal y en la barra sticky.
     * No es el precio unitario solo, ni el total del carrito.
     */
    updateBuyButtonPrice() {
        if (!this.currentUnitPrice) return;
        const qty = this.currentQuantity || 1;
        const total = this.getDisplayedUnitPrice() * qty;
        const compareTotal = this.currentCompareAtPrice ? this.currentCompareAtPrice * qty : 0;

        const mainPrice = document.querySelector('[data-buy-button-price]');
        if (mainPrice) mainPrice.textContent = this.formatMoneyShopify(total);

        const mainCompare = document.querySelector('[data-buy-button-compare]');
        if (mainCompare) {
            if (this.hideCompareAt) {
                mainCompare.style.display = 'none';
            } else if (compareTotal > total) {
                mainCompare.textContent = this.formatMoneyShopify(compareTotal);
                mainCompare.style.display = '';
            } else {
                mainCompare.style.display = 'none';
            }
        }

        const stickyPrice = document.querySelector('[data-sticky-price]');
        if (stickyPrice) stickyPrice.textContent = this.formatMoneyShopify(total);

        const stickyCompare = document.querySelector('[data-sticky-compare]');
        if (stickyCompare) {
            if (this.hideCompareAt) {
                stickyCompare.style.display = 'none';
            } else if (compareTotal > total) {
                stickyCompare.textContent = this.formatMoneyShopify(compareTotal);
                stickyCompare.style.display = '';
            } else {
                stickyCompare.style.display = 'none';
            }
        }
    }

    /**
     * Sticky Buy Bar - Show when fold section scrolls out
     */
    setupStickyObserver() {
        if (!this.foldSection || !this.stickyBuyBar) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.boundingClientRect.bottom < 0) {
                        this.stickyBuyBar.classList.add('visible');
                    } else {
                        this.stickyBuyBar.classList.remove('visible');
                    }
                });
            },
            { threshold: 0, rootMargin: '0px' }
        );

        observer.observe(this.foldSection);
    }

    /**
     * Anchor Navigation - Smooth scroll to sections
     */
    setupAnchorNavigation() {
        this.anchorChips.forEach(chip => {
            chip.addEventListener('click', (e) => {
                e.preventDefault();
                const targetId = chip.dataset.anchorId;
                this.scrollToSection(targetId);
                this.setActiveChip(chip);
            });
        });

        this.setupScrollSpy();
    }

    scrollToSection(sectionId) {
        const targetElement = document.getElementById(sectionId);
        if (!targetElement) return;

        const headerOffset = 140;
        const elementPosition = targetElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }

    setActiveChip(activeChip) {
        this.anchorChips.forEach(chip => chip.classList.remove('active'));
        activeChip.classList.add('active');
    }

    setupScrollSpy() {
        // Optional: Auto-highlight chips based on scroll position
    }

    /**
     * Image Gallery - Thumbnail click to change main image
     */
    setupImageGallery() {
        this.thumbnails.forEach((thumbnail, index) => {
            thumbnail.addEventListener('click', () => {
                this.changeMainImage(thumbnail, index);
            });
        });
    }

    changeMainImage(thumbnail, index) {
        if (!this.mainImage) return;

        this.thumbnails.forEach(t => t.classList.remove('active'));
        thumbnail.classList.add('active');

        const thumbImg = thumbnail.querySelector('img');
        // La miniatura puede emitirse a cualquier width; subimos ese width en vez
        // de depender de un literal ('width=200') que se rompe al tocar el Liquid.
        const newImageSrc = thumbnail.dataset.fullMedia
            || (thumbImg && thumbImg.src.replace(/([?&]width=)\d+/, '$1800'))
            || (thumbImg && thumbImg.src);
        if (!newImageSrc) return;

        this.mainImage.classList.add('changing');

        setTimeout(() => {
            // El <img> principal trae srcset+sizes: si no los quitamos, el navegador
            // sigue resolviendo la imagen desde el srcset (la destacada original) y
            // reasignar .src no se ve.
            this.mainImage.removeAttribute('srcset');
            this.mainImage.removeAttribute('sizes');
            this.mainImage.src = newImageSrc;
            this.mainImage.classList.remove('changing');
        }, 150);
    }

    /**
     * Bundle Selector - Handle quantity selection
     */
    setupBundleSelector() {
        this.bundleCards.forEach(card => {
            card.addEventListener('click', () => {
                this.selectBundle(card);
            });
        });
    }

    selectBundle(selectedCard) {
        this.bundleCards.forEach(card => card.classList.remove('selected'));
        selectedCard.classList.add('selected');

        this.currentQuantity = parseInt(selectedCard.dataset.quantity) || 1;
        this.currentTotalPrice = parseInt(selectedCard.dataset.totalPrice) || 0;
        this.currentComparePrice = parseInt(selectedCard.dataset.comparePrice) || 0;

        this.updatePrices();
    }

    selectDefaultBundle() {
        if (this.bundleCards.length >= 2) {
            this.selectBundle(this.bundleCards[1]);
        } else if (this.bundleCards.length === 1) {
            this.selectBundle(this.bundleCards[0]);
        }
    }

    updatePrices() {
        const stickyPrice = this.stickyBuyBar?.querySelector('[data-sticky-price]');
        if (stickyPrice && this.currentTotalPrice) {
            stickyPrice.textContent = this.formatPrice(this.currentTotalPrice);
        }

        const mainPrice = document.querySelector('[data-main-price]');
        if (mainPrice && this.currentTotalPrice) {
            mainPrice.textContent = this.formatPrice(this.currentTotalPrice);
        }

        const comparePrice = document.querySelector('[data-compare-price]:not([data-product-section])');
        if (comparePrice) {
            if (this.hideCompareAt) {
                comparePrice.style.display = 'none';
            } else if (this.currentComparePrice > this.currentTotalPrice) {
                comparePrice.textContent = this.formatPrice(this.currentComparePrice);
                comparePrice.style.display = 'inline-block';
            } else {
                comparePrice.style.display = 'none';
            }
        }
    }

    formatPrice(cents) {
        const dollars = (cents / 100).toFixed(0);
        return `$${parseInt(dollars).toLocaleString('es-CO')}.000`;
    }

    /**
     * Variant Picker - Sync selected variant with cart actions
     */
    setupVariantPicker() {
        const variantDataEl = document.querySelector('[data-product-variants]');
        if (!variantDataEl) {
            console.warn('⚠️ No se encontró [data-product-variants]. Variant picker no inicializado.');
            return;
        }

        let variants = [];
        try {
            variants = JSON.parse(variantDataEl.textContent);
        } catch (e) {
            console.error('❌ Error parseando variantes:', e);
            return;
        }

        const pickerContainer = document.querySelector('[data-variant-picker]');
        if (!pickerContainer) return;

        const getSelectedVariant = () => {
            const selectedOptions = [];

            pickerContainer.querySelectorAll('.rg-variant-picker__option-group').forEach(group => {
                const radio = group.querySelector('.rg-variant-picker__radio:checked');
                const select = group.querySelector('.rg-variant-picker__select');

                if (radio) {
                    selectedOptions.push(radio.value);
                } else if (select) {
                    selectedOptions.push(select.value);
                }
            });

            return variants.find(v =>
                v.options.every((opt, i) => opt === selectedOptions[i])
            ) || null;
        };

        const applyVariant = (variant) => {
            if (!variant) return;

            this.currentVariantId = variant.id;
            console.log('🔄 Variante cambiada:', variant.title, '| ID:', variant.id);

            const hiddenInput = document.querySelector('[data-selected-variant]');
            if (hiddenInput) hiddenInput.value = variant.id;

            if (this.productSection) {
                this.productSection.dataset.variantId = variant.id;
            }

            this.currentUnitPrice = variant.price;
            this.currentCompareAtPrice = variant.compare_at_price || 0;
            this.renderDisplayedPrice();
            this.syncPromoFromCart();

            const comparePrice = document.querySelector('[data-compare-price]:not([data-product-section])');
            if (comparePrice) {
                if (this.hideCompareAt) {
                    comparePrice.style.display = 'none';
                } else if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                    comparePrice.textContent = this.formatMoneyShopify(variant.compare_at_price);
                    comparePrice.style.display = 'inline-block';
                } else {
                    comparePrice.style.display = 'none';
                }
            }

            const stickyPrice = document.querySelector('[data-sticky-price]');
            if (variant.featured_image && this.mainImage) {
                this.mainImage.removeAttribute('srcset');
                this.mainImage.removeAttribute('sizes');
                this.mainImage.src = variant.featured_image.src;

                this.thumbnails.forEach(thumb => {
                    const thumbIndex = parseInt(thumb.dataset.thumbnailIndex);
                    if (thumbIndex === variant.featured_image.position - 1) {
                        this.changeMainImage(thumb, thumbIndex);
                    }
                });
            }

            const buyBtn = document.querySelector('[data-buy-now-main]');
            const atcBtn = document.querySelector('[data-add-to-cart]');
            const unavailable = !variant.available;

            if (buyBtn) {
                buyBtn.disabled = unavailable;
                const label = buyBtn.querySelector('.rg-product-hero__buy-button-label');
                const priceGroup = buyBtn.querySelector('.rg-product-hero__buy-button-price-group');
                if (label) label.textContent = unavailable ? 'AGOTADO' : (buyBtn.dataset.label || 'COMPRAR AHORA');
                if (priceGroup) priceGroup.style.display = unavailable ? 'none' : '';
            }
            if (atcBtn) {
                atcBtn.disabled = unavailable;
                if (unavailable) {
                    if (!atcBtn.dataset.originalText) atcBtn.dataset.originalText = atcBtn.textContent;
                    atcBtn.textContent = 'AGOTADO';
                } else if (atcBtn.dataset.originalText) {
                    atcBtn.textContent = atcBtn.dataset.originalText;
                }
            }

            const stickyBtn = document.querySelector('[data-buy-now-sticky]');
            if (stickyBtn) {
                stickyBtn.disabled = unavailable;
                if (unavailable) {
                    if (!stickyBtn.dataset.originalText) stickyBtn.dataset.originalText = stickyBtn.textContent.trim();
                    stickyBtn.textContent = 'AGOTADO';
                } else if (stickyBtn.dataset.originalText) {
                    stickyBtn.textContent = stickyBtn.dataset.originalText;
                }
            }
        };

        pickerContainer.addEventListener('change', (e) => {
            if (
                e.target.classList.contains('rg-variant-picker__radio') ||
                e.target.classList.contains('rg-variant-picker__select')
            ) {
                const variant = getSelectedVariant();
                applyVariant(variant);
            }
        });

        const initialVariant = getSelectedVariant();
        if (initialVariant) {
            this.currentVariantId = initialVariant.id;
            console.log('✅ Variante inicial confirmada:', initialVariant.title, '| ID:', initialVariant.id);
        }
    }

    /**
     * Formatea centavos a moneda colombia
     * Ejemplo: 8990000 → "$89.900"
     */
    formatMoneyShopify(cents) {
        if (!cents) return '';
        return '$' + (cents / 100).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    }

    /**
     * Cart Actions - Add to Cart & Buy Now
     */
    setupCartActions() {
        console.log('🔘 Setting up cart actions - Buy buttons:', this.buyNowButtons.length, 'Add to cart:', this.addToCartButton ? 'Found' : 'Not found');

        this.buyNowButtons.forEach(button => {
            button.addEventListener('click', () => {
                this.handleBuyNow();
            });
        });

        if (this.addToCartButton) {
            this.addToCartButton.addEventListener('click', () => {
                this.handleAddToCart();
            });
        }
    }

    async handleBuyNow() {
        console.log('🛒 Buy Now clicked - Variant:', this.currentVariantId, 'Quantity:', this.currentQuantity);

        if (!this.currentVariantId) {
            console.error('No variant selected');
            return;
        }

        const quantity = this.currentQuantity || 1;

        try {
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: this.currentVariantId,
                    quantity: quantity
                })
            });

            if (response.ok) {
                window.location.href = '/checkout';
            } else {
                console.error('Failed to add to cart', response);
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
        }
    }

    async handleAddToCart() {
        console.log('➕ Add to Cart clicked - Variant:', this.currentVariantId, 'Quantity:', this.currentQuantity);

        if (!this.currentVariantId) {
            console.error('No variant selected');
            return;
        }

        const quantity = this.currentQuantity || 1;
        const btn = this.addToCartButton;
        const originalText = btn.innerHTML;

        btn.innerHTML = '<span class="material-icons-outlined rg-spin">sync</span> AGREGANDO...';
        btn.disabled = true;

        try {
            const sectionsToUpdate = ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble', 'cart-live-region-text', 'main-cart-items'];

            const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: [{
                        id: this.currentVariantId,
                        quantity: quantity
                    }],
                    sections: sectionsToUpdate
                })
            });

            if (response.ok) {
                const data = await response.json();

                if (window.publish && window.PUB_SUB_EVENTS) {
                    window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                        source: 'rg-product-hero',
                        cartData: data
                    });
                }

                const miniCart = document.querySelector('mini-cart');
                if (miniCart) {
                    if (typeof miniCart.renderContents === 'function') {
                        miniCart.renderContents(data);
                    }
                    if (typeof miniCart.open === 'function') {
                        miniCart.open();
                    }
                }

                if (data.sections) {
                    if (data.sections['cart-icon-bubble']) {
                        const cartIconBubble = document.getElementById('cart-icon-bubble') || document.querySelector('.cart-icon-bubble');
                        if (cartIconBubble) cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
                    }

                    const fetchCart = await fetch(window.Shopify.routes.root + 'cart.js');
                    const cartData = await fetchCart.json();
                    document.querySelectorAll('.cart-count-bubble span, [data-cart-count]').forEach(el => {
                        el.innerText = cartData.item_count;
                        el.closest('.cart-count-bubble')?.classList.remove('hidden');
                    });
                }

                await this.syncPromoFromCart();

                btn.innerHTML = '<span class="material-icons-outlined">check_circle</span> ¡LISTO!';
                btn.classList.add('rg-success');

            } else {
                throw new Error('Network response was not ok');
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
            btn.innerHTML = '<span class="material-icons-outlined">error</span> ERROR';
        } finally {
            setTimeout(() => {
                btn.innerHTML = originalText;
                btn.disabled = false;
                btn.classList.remove('rg-success');
            }, 3000);
        }
    }

    /**
     * Scroll To Buttons
     */
    setupScrollToButtons() {
        this.scrollToButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                const targetId = button.dataset.scrollTo;
                this.scrollToSection(targetId);
            });
        });
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new ProductHero();
    });
} else {
    new ProductHero();
}


/* ===== Miracles · Galeria Product Hero: swipe + flechas liquid glass ===== */
(function () {
  var STYLE_ID = 'rg-gallery-arrows-style';

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var css =
      '.rg-gal-arrow{position:absolute;top:50%;transform:translateY(-50%);z-index:6;' +
      'width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;' +
      'padding:0;cursor:pointer;color:rgb(var(--color-foreground,26,27,24));' +
      'border:1px solid rgba(255,255,255,.55);' +
      'background:rgba(var(--color-background,255,255,255),.35);' +
      '-webkit-backdrop-filter:blur(12px) saturate(180%);backdrop-filter:blur(12px) saturate(180%);' +
      'box-shadow:0 4px 16px rgba(0,0,0,.14),inset 0 1px 1px rgba(255,255,255,.7),inset 0 -1px 2px rgba(255,255,255,.25);' +
      'transition:transform .18s ease,background .18s ease,box-shadow .18s ease,opacity .18s ease;' +
      '-webkit-tap-highlight-color:transparent;}' +
      '.rg-gal-arrow:hover{background:rgba(var(--color-background,255,255,255),.55);' +
      'transform:translateY(-50%) scale(1.08);box-shadow:0 6px 20px rgba(0,0,0,.18),inset 0 1px 1px rgba(255,255,255,.8);}' +
      '.rg-gal-arrow:active{transform:translateY(-50%) scale(.94);}' +
      '.rg-gal-arrow svg{width:17px;height:17px;display:block;}' +
      '.rg-gal-arrow--prev{left:12px;}' +
      '.rg-gal-arrow--next{right:12px;}' +
      '@media (max-width:749px){.rg-gal-arrow{width:30px;height:30px;}.rg-gal-arrow svg{width:15px;height:15px;}.rg-gal-arrow--prev{left:8px;}.rg-gal-arrow--next{right:8px;}}' +
      '@media (prefers-reduced-motion:reduce){.rg-gal-arrow{transition:none;}}';
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = css;
    document.head.appendChild(s);
  }

  function makeArrow(dir) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'rg-gal-arrow rg-gal-arrow--' + dir;
    b.setAttribute('aria-label', dir === 'prev' ? 'Imagen anterior' : 'Imagen siguiente');
    var d = dir === 'prev' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6';
    b.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="' + d + '"></path></svg>';
    return b;
  }

  function init() {
    var main = document.getElementById('rg-main-image');
    var thumbs = Array.prototype.slice.call(document.querySelectorAll('[data-thumbnail-index]'));
    if (!main || thumbs.length < 2 || main.__rgSwipe) return;
    main.__rgSwipe = true;
    main.style.touchAction = 'pan-y';

    function go(dir) {
      var idx = -1;
      for (var i = 0; i < thumbs.length; i++) { if (thumbs[i].classList.contains('active')) { idx = i; break; } }
      if (idx < 0) idx = 0;
      var n = (idx + dir + thumbs.length) % thumbs.length;
      thumbs[n].click();
    }

    var x0 = 0, y0 = 0, track = false;
    var TH = 40;
    main.addEventListener('touchstart', function (e) {
      var t = e.touches[0]; x0 = t.clientX; y0 = t.clientY; track = true;
    }, { passive: true });
    main.addEventListener('touchend', function (e) {
      if (!track) return; track = false;
      var t = e.changedTouches[0]; var dx = t.clientX - x0, dy = t.clientY - y0;
      if (Math.abs(dx) < TH || Math.abs(dx) < Math.abs(dy) * 1.4) return;
      go(dx < 0 ? 1 : -1);
    }, { passive: true });
    main.addEventListener('mousedown', function (e) { x0 = e.clientX; y0 = e.clientY; track = true; e.preventDefault(); });
    window.addEventListener('mouseup', function (e) {
      if (!track) return; track = false;
      var dx = e.clientX - x0, dy = e.clientY - y0;
      if (Math.abs(dx) < TH || Math.abs(dx) < Math.abs(dy) * 1.4) return;
      go(dx < 0 ? 1 : -1);
    });
    main.addEventListener('dragstart', function (e) { e.preventDefault(); });

    injectStyle();
    var container = main.parentElement;
    if (container) {
      if (window.getComputedStyle(container).position === 'static') container.style.position = 'relative';
      var prev = makeArrow('prev');
      var next = makeArrow('next');
      prev.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); go(-1); });
      next.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); go(1); });
      container.appendChild(prev);
      container.appendChild(next);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();

// Mini upsells use event delegation so they also work after a Theme Editor reload.
// Keeping this behavior in the section asset avoids inline scripts and unsafe HTML injection.
if (!window.__rgProductHeroMiniUpsellsBound) {
    window.__rgProductHeroMiniUpsellsBound = true;

    document.addEventListener('click', async (event) => {
        const button = event.target.closest('[data-mini-upsell-add]');
        if (!button || button.disabled) return;

        const card = button.closest('[data-variant-id]');
        const variantId = card?.dataset.variantId;
        if (!variantId) return;

        const originalLabel = button.textContent;
        button.disabled = true;
        button.classList.add('adding');
        button.textContent = '…';

        try {
            const root = window.Shopify?.routes?.root || '/';
            const response = await fetch(root + 'cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: variantId, quantity: 1 })
            });

            if (!response.ok) throw new Error('Could not add mini upsell to cart');

            const cart = await response.json();
            if (window.publish && window.PUB_SUB_EVENTS) {
                window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'rg-mini-upsell', cartData: cart });
            }

            button.classList.remove('adding');
            button.classList.add('added');
            button.textContent = '✓';
        } catch (error) {
            console.error('Mini upsell:', error);
            button.classList.remove('adding');
            button.textContent = '×';
        }

        window.setTimeout(() => {
            button.textContent = originalLabel;
            button.disabled = false;
            button.classList.remove('adding', 'added');
        }, 1500);
    });
}
